/* KN size guide: one delegated trigger handler survives Dawn's variant replacements. */
(() => {
  if (customElements.get('kn-size-guide')) return;
  const pending = new Map();
  const parse = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const charts = {};
    for (const key of ['clothing', 'shoes']) {
      const table = doc.querySelector(`table[data-ksg-chart="${key}"]`);
      if (!table?.querySelector('tbody tr') || !table.querySelector('thead th')) throw new Error('Missing size chart');
      table.className = 'ksg-data-table';
      table.querySelectorAll('[id], [data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('id');
        node.removeAttribute('data-shopify-editor-block');
      });
      charts[key] = table.outerHTML;
    }
    const tips = [...doc.querySelectorAll('[data-ksg-tip]')].map((tip) => ({
      key: tip.dataset.ksgTip,
      title: tip.querySelector('b')?.textContent || '',
      text: tip.querySelector('span')?.textContent || '',
    }));
    if (!tips.some((tip) => tip.key === 'between-sizes' && tip.text)) throw new Error('Missing size tip');
    return { charts, tips };
  };
  const load = (guide) => {
    let url;
    try { url = new URL(guide.dataset.source, location.origin); }
    catch (_) { return Promise.reject(new Error('Invalid chart URL')); }
    if (url.origin !== location.origin || !url.searchParams.has('section_id')) return Promise.reject(new Error('Invalid chart URL'));
    const key = `kn-size-guide:v1:${window.Shopify?.theme?.id || ''}:${url.href}`;
    if (pending.has(key)) return pending.get(key);
    const request = (async () => {
      try {
        const saved = sessionStorage.getItem(key);
        if (saved) return parse(saved);
      } catch (_) { /* Private browsing may disable session storage. */ }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        // This independent Help request does not need the referring product URL.
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', referrerPolicy: 'no-referrer' });
        if (!response.ok) throw new Error('Size guide unavailable');
        const html = await response.text();
        const data = parse(html);
        try { sessionStorage.setItem(key, html); } catch (_) { /* Memory cache still works. */ }
        return data;
      } finally { clearTimeout(timeout); }
    })();
    pending.set(key, request);
    request.catch(() => pending.delete(key));
    return request;
  };

  customElements.define('kn-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.panel = this.querySelector('.ksg-panel');
      this.tabs = [...this.querySelectorAll('[data-ksg-tab]')];
      this.events = new AbortController();
      this.generation = 0;
      const options = { signal: this.events.signal };
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-ksg-close]')) this.close();
        const tab = event.target.closest('[data-ksg-tab]');
        if (tab) this.select(tab.dataset.ksgTab);
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.dialog.addEventListener('close', () => this.unlock(), options);
    }

    disconnectedCallback() {
      ++this.generation;
      clearTimeout(this.closeTimer);
      this.dialog?.close();
      this.unlock();
      this.events?.abort();
    }

    select(chart) {
      this.chart = chart;
      this.tabs.forEach((tab) => {
        const active = tab.dataset.ksgTab === chart;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-ksg-chart-panel]').forEach((panel) => {
        panel.hidden = !this.loaded || panel.dataset.ksgChartPanel !== chart;
      });
    }

    async open(trigger) {
      if (this.dialog.open) return;
      const generation = ++this.generation;
      this.trigger = trigger;
      clearTimeout(this.closeTimer);
      this.select(this.dataset.defaultChart);
      this.dialog.showModal();
      this.lock();
      requestAnimationFrame(() => {
        if (this.dialog.open && this.generation === generation) {
          this.dialog.classList.add('open');
          this.panel.focus({ preventScroll: true });
        }
      });
      try {
        const data = await load(this);
        if (!this.isConnected || this.generation !== generation) return;
        if (!this.loaded) {
          this.querySelectorAll('[data-ksg-chart-panel]').forEach((panel) => {
            panel.querySelector('.ksg-table').innerHTML = data.charts[panel.dataset.ksgChartPanel];
          });
          const tip = data.tips.find((entry) => entry.key === 'between-sizes');
          const target = this.querySelector('[data-ksg-shared-tip]');
          const title = document.createElement('b');
          title.textContent = tip.title;
          target.replaceChildren(title, document.createTextNode(` ${tip.text}`));
          this.querySelector('[data-ksg-loading]').hidden = true;
          this.querySelector('.ksg-tips').hidden = false;
          this.loaded = true;
          this.select(this.chart);
        }
        if (typeof window.dataLayer?.push === 'function') window.dataLayer.push({
          event: 'size_guide_open', product_id: this.dataset.productId,
          product_type: this.dataset.productType, chart: this.chart,
        });
      } catch (_) {
        if (this.isConnected && this.generation === generation) {
          this.close(true);
          window.location.assign(trigger.href);
        }
      }
    }

    close(immediate = false) {
      if (!this.dialog.open) return;
      ++this.generation;
      this.dialog.classList.remove('open');
      const finish = () => {
        this.dialog.close();
        this.unlock();
        const trigger = this.trigger?.isConnected ? this.trigger : [...document.querySelectorAll('[data-size-guide]')].find((node) => node.dataset.sizeGuide === this.id);
        trigger?.focus({ preventScroll: true });
      };
      clearTimeout(this.closeTimer);
      if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else this.closeTimer = setTimeout(finish, 320);
    }

    lock() {
      const body = document.body;
      this.scroll = { x: scrollX, y: scrollY, styles: body.getAttribute('style') };
      const gap = innerWidth - document.documentElement.clientWidth;
      const padding = parseFloat(getComputedStyle(body).paddingRight);
      Object.assign(body.style, { position: 'fixed', top: `${-this.scroll.y}px`, left: `${-this.scroll.x}px`, width: '100%', overflow: 'hidden' });
      if (gap) body.style.paddingRight = `${padding + gap}px`;
    }

    unlock() {
      if (!this.scroll) return;
      const state = this.scroll;
      this.scroll = null;
      if (state.styles === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', state.styles);
      const root = document.documentElement;
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(state.x, state.y);
      root.style.scrollBehavior = behavior;
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-ksg-tab]');
      if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const current = this.tabs.indexOf(tab);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next].dataset.ksgTab);
        this.tabs[next].focus();
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('a[href], button, [tabindex="0"]')].filter((node) => node.tabIndex >= 0 && node.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if ((event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    }
  });

  const guideFor = (target) => {
    const trigger = target instanceof Element && target.closest('[data-size-guide]');
    if (!trigger || trigger.closest('quick-add-modal')) return {};
    const guide = document.getElementById(trigger.dataset.sizeGuide);
    if (!guide?.dialog || typeof guide.dialog.showModal !== 'function') return {};
    return { trigger, guide };
  };
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const { trigger, guide } = guideFor(event.target);
    if (!guide) return;
    event.preventDefault();
    guide.open(trigger);
  });
  const prefetch = (event) => {
    const { guide } = guideFor(event.target);
    if (guide) load(guide).catch(() => {});
  };
  document.addEventListener('pointerenter', prefetch, true);
  document.addEventListener('focusin', prefetch);
})();
